import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { useNavigate } from 'react-router-dom'
import './PurchaseSection.css'
import httpClient from '../../../helpers/httpClient'
import LoadingSpinner from '../../../placeholders/LoadingSpinner'
import Label from '../../../Label'
import { formatDate } from '../../../helpers/formatDate'

const PurchaseSection = forwardRef(({ unit }, ref) => {
  useImperativeHandle(ref, () => ({}))

  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [buyersByUnitId, setBuyersByUnitId] = useState({})

  useEffect(() => {
    const fetchPurchaseOrders = async () => {
      if (!unit || !unit.unit_name) return
      setIsLoading(true)
      try {
        const query = new URLSearchParams({
          page: '1',
          limit: '100',
          search: unit.unit_name,
        }).toString()
        const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/purchase-orders?${query}`)
        setPurchaseOrders(response.data.purchaseOrders || [])
      } finally {
        setIsLoading(false)
      }
    }

    fetchPurchaseOrders()
  }, [unit?.unit_name])

  useEffect(() => {
    const fetchBuyersForUnits = async () => {
      const uniqueUnitIds = Array.from(new Set(
        (purchaseOrders || [])
          .map(po => po?.unit_id?.id)
          .filter(id => !!id)
      ))

      if (uniqueUnitIds.length === 0) return

      try {
        const results = await Promise.all(
          uniqueUnitIds.map(async (uid) => {
            try {
              const res = await httpClient.get(import.meta.env.VITE_API_URL + `/api/units/get/${uid}`)
              return [uid, Array.isArray(res.data?.buyers) ? res.data.buyers : []]
            } catch {
              return [uid, []]
            }
          })
        )

        const map = results.reduce((acc, [uid, buyers]) => {
          acc[uid] = buyers
          return acc
        }, {})
        setBuyersByUnitId(map)
      } catch {
        // ignore buyers fetch errors to not block the UI
      }
    }

    fetchBuyersForUnits()
  }, [purchaseOrders])

  const handleOnClickRow = (poID) => {
    navigate(`/purchase-orders/${poID}`)
  }

  return (
    <div className='popout_body__content_container' id="unit-purchase-section">
      <h2>Purchase Orders</h2>
      <div className='table'>
        <div className='table__header-4'>
          <div className='table__header--field'>PO Number</div>
          <div className='table__header--field'>Created on</div>
          <div className='table__header--field'>Buyer(s)</div>
          <div className='table__header--field'>Status</div>
        </div>
        <div className='table__rows_container'>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            purchaseOrders.map((po, index) => (
              <div
                className='table__row-4'
                key={index}
                onClick={() => handleOnClickRow(po.id)}
                data-po-id={po.id}
              >
                <div className='table__row--field'>{po.id || 'N/A'}</div>
                <div className='table__row--field'>{po.timestamp ? formatDate(po.timestamp) : 'N/A'}</div>
                <div className='table__row--field'>
                  {(() => {
                    const unitId = po?.unit_id?.id
                    const buyers = unitId ? buyersByUnitId[unitId] : []
                    const names = Array.isArray(buyers)
                      ? buyers.map(b => {
                          if (b?.type === 'contact') {
                            const first = b.first_name || ''
                            const last = b.last_name || ''
                            return `${first} ${last}`.trim()
                          }
                          if (b?.type === 'company') {
                            return b.company_name || null
                          }
                          // Fallback if API shape is different
                          if (b?.first_name || b?.last_name) {
                            return `${b.first_name || ''} ${b.last_name || ''}`.trim()
                          }
                          return b?.company_name || null
                        }).filter(Boolean)
                      : []

                    return names.length > 0 ? (
                      <div className='labels-container'>
                        {names.map((n, i) => (
                          <span key={i} className='label-item'>{n}</span>
                        ))}
                      </div>
                    ) : 'N/A'
                  })()}
                </div>
                <div className='table__row--field'>
                  {po.status ? (
                    <Label title={po.status} labelColor={{
                      draft: 'gray',
                      'for signature': 'orange',
                      signed: 'green',
                      rejected: 'red',
                      cancelled: 'darkgray',
                    }[po.status] || 'gray'} />
                  ) : null}
                </div>
              </div>
            ))
          )}
          {!isLoading && purchaseOrders.length === 0 && (
            <div className='table__row-1'>
              <div className='table__row--field'>No purchase orders linked to this unit.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export default PurchaseSection